import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { format, isSameDay } from "date-fns";

export type YearMonthCalendarProps = React.ComponentProps<typeof DayPicker> & {
  onMonthChange?: (date: Date) => void;
  showRenewalStatus?: boolean;
  hideSelectors?: boolean;
};

function YearMonthCalendar({
  className,
  classNames,
  showOutsideDays = true,
  onMonthChange,
  showRenewalStatus = false,
  hideSelectors = false,
  ...props
}: YearMonthCalendarProps) {
  // Generate years array for dropdown (10 years past, 10 years future)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);
  
  // Get current displayed month from props or default to current month
  const currentMonth = props.month || new Date();
  const [selectedYear, setSelectedYear] = React.useState(currentMonth.getFullYear());
  const [selectedMonth, setSelectedMonth] = React.useState(currentMonth.getMonth());
  
  // Generate months for dropdown
  const months = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];

  // Only fetch renewals data if we're showing renewal status
  const { data: renewals } = useQuery({
    queryKey: ['/api/renewals'],
    select: (data) => data || [],
    enabled: showRenewalStatus
  });

  // Get all renewal dates with associated license info
  const getRenewalDates = () => {
    if (!renewals || !Array.isArray(renewals)) return [];
    
    return renewals.map((license: any) => {
      const renewalDate = new Date(license.renewalDate);
      return {
        date: renewalDate,
        license: license
      };
    });
  };

  // Handle year change
  const handleYearChange = (year: string) => {
    const newYear = parseInt(year);
    setSelectedYear(newYear);
    
    // Create new date with selected year/month
    const newDate = new Date(currentMonth);
    newDate.setFullYear(newYear);
    
    // Update calendar display
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  // Handle month change
  const handleMonthChange = (month: string) => {
    const monthIndex = months.findIndex(m => m === month);
    if (monthIndex !== -1) {
      setSelectedMonth(monthIndex);
      
      // Create new date with selected year/month
      const newDate = new Date(currentMonth);
      newDate.setMonth(monthIndex);
      
      // Update calendar display
      if (onMonthChange) {
        onMonthChange(newDate);
      }
    }
  };

  // Update local state when month prop changes
  React.useEffect(() => {
    if (props.month) {
      setSelectedYear(props.month.getFullYear());
      setSelectedMonth(props.month.getMonth());
    }
  }, [props.month]);

  // Define modifiers for different renewal urgency levels
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const urgentRenewalDates = React.useMemo(() => {
    if (!showRenewalStatus || !renewals) return [];
    
    return getRenewalDates()
      .filter(item => {
        if (!item.date) return false;
        
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        
        const diffTime = itemDate.getTime() - today.getTime();
        const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return daysUntil >= 0 && daysUntil <= 7;
      })
      .map(item => item.date);
  }, [renewals, showRenewalStatus]);
  
  const soonRenewalDates = React.useMemo(() => {
    if (!showRenewalStatus || !renewals) return [];
    
    return getRenewalDates()
      .filter(item => {
        if (!item.date) return false;
        
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        
        const diffTime = itemDate.getTime() - today.getTime();
        const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return daysUntil > 7 && daysUntil <= 30;
      })
      .map(item => item.date);
  }, [renewals, showRenewalStatus]);
  
  const futureRenewalDates = React.useMemo(() => {
    if (!showRenewalStatus || !renewals) return [];
    
    return getRenewalDates()
      .filter(item => {
        if (!item.date) return false;
        
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        
        const diffTime = itemDate.getTime() - today.getTime();
        const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return daysUntil > 30;
      })
      .map(item => item.date);
  }, [renewals, showRenewalStatus]);

  return (
    <div className="space-y-4">
      {/* Month and Year selectors */}
      {!hideSelectors && (
        <div className="flex justify-center items-center space-x-2 px-2">
          <Select 
            value={months[selectedMonth]}
            onValueChange={handleMonthChange}
          >
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue>{months[selectedMonth]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {months.map((month, index) => (
                <SelectItem key={index} value={month} className="text-xs">
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select 
            value={selectedYear.toString()}
            onValueChange={handleYearChange}
          >
            <SelectTrigger className="w-[80px] h-8 text-xs">
              <SelectValue>{selectedYear}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()} className="text-xs">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* Custom styling for color-coded renewal dates */}
      {showRenewalStatus && (
        <style dangerouslySetInnerHTML={{ __html: `
          /* Urgent renewal (within 7 days) */
          .urgent-renewal-day {
            background-color: rgba(254, 226, 226, 0.8) !important;
            color: rgb(185, 28, 28) !important;
            border-radius: 6px !important;
            font-weight: 600 !important;
          }
          
          /* Soon renewal (this month) */
          .soon-renewal-day {
            background-color: rgba(254, 249, 195, 0.8) !important;
            color: rgb(161, 98, 7) !important;
            border-radius: 6px !important;
            font-weight: 600 !important;
          }
          
          /* Future renewal */
          .future-renewal-day {
            background-color: rgba(220, 252, 231, 0.8) !important;
            color: rgb(22, 101, 52) !important;
            border-radius: 6px !important;
            font-weight: 600 !important;
          }
          
          /* Today with renewal */
          .rdp-day_today.urgent-renewal-day,
          .rdp-day_today.soon-renewal-day,
          .rdp-day_today.future-renewal-day {
            border: 2px solid #000 !important;
            font-weight: bold !important;
          }
          
          /* Customize the overall calendar appearance to match the example */
          .rdp-months {
            background-color: #f8fafc !important;
            border-radius: 12px !important;
            padding: 10px !important;
          }
          
          .rdp-day {
            border-radius: 6px !important;
            height: 40px !important;
            width: 40px !important;
            margin: 2px !important;
            font-size: 15px !important;
          }
          
          .rdp-day_outside {
            opacity: 0.4 !important;
          }
          
          .rdp-head_cell {
            font-weight: 600 !important;
            color: #64748b !important;
            font-size: 14px !important;
          }
        `}} />
      )}
      
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn("p-3", className)}
        modifiers={{
          urgentRenewal: urgentRenewalDates,
          soonRenewal: soonRenewalDates,
          futureRenewal: futureRenewalDates
        }}
        modifiersClassNames={{
          urgentRenewal: 'urgent-renewal-day',
          soonRenewal: 'soon-renewal-day',
          futureRenewal: 'future-renewal-day'
        }}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "flex justify-center pt-1 relative items-center hidden", // Hide default caption since we render our own
          caption_label: "text-sm font-medium",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          ),
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell:
            "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
          row: "flex w-full mt-2",
          cell: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
          day: cn(
            buttonVariants({ variant: "ghost" }),
            "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
          ),
          day_selected:
            "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
          day_today: "bg-accent text-accent-foreground font-bold",
          day_outside: "text-muted-foreground opacity-50",
          day_disabled: "text-muted-foreground opacity-50",
          day_range_middle:
            "aria-selected:bg-accent aria-selected:text-accent-foreground",
          day_hidden: "invisible",
          ...classNames,
        }}
        components={{
          IconLeft: ({ ...props }) => <ChevronLeft className="h-4 w-4" />,
          IconRight: ({ ...props }) => <ChevronRight className="h-4 w-4" />
        }}
        {...props}
      />
    </div>
  );
}

YearMonthCalendar.displayName = "YearMonthCalendar";

export { YearMonthCalendar };